import { useEffect, useState, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTabsStore } from '@/app/store/useTabsStore';
import { TabBar } from '../TabBar';
import { TabPanel } from '../TabPanel';
import { getComponentForRoute } from '@/app/routes/tabRouteRegistry';
import { useToast } from '@/shared/components/ui';
import styles from './TabContainer.module.css';

/**
 * Props para TabContainer
 */
export interface TabContainerProps {
    getTabTitle?: (route: string) => string;
    getTabIcon?: (route: string) => string | undefined;
    isSidebarCollapsed?: boolean;
}

/**
 * TabContainer - Contenedor orquestador del sistema de tabs
 * 
 * ARQUITECTURA CLAVE:
 * - Solo monta componentes cuando el tab es ACTIVO por primera vez
 * - Una vez montado, permanece montado (preserva estado)
 * - Usa display:none para ocultar inactivos
 * - Evita montar todos los componentes simultáneamente (causa llamadas duplicadas)
 */
export const TabContainer = ({
    getTabTitle,
    getTabIcon,
    isSidebarCollapsed = false,
}: TabContainerProps = {}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { tabs, activeTabId, openTab, setActiveTab, getTabByRoute, closeTab } =
        useTabsStore();

    /**
     * Set de IDs de tabs que han sido activados al menos una vez
     * Solo estos tabs tendrán su componente montado
     */
    const [mountedTabIds, setMountedTabIds] = useState<Set<string>>(new Set());

    /**
     * Inicializar Dashboard como tab fijo
     */
    useEffect(() => {
        const dashboardTab = getTabByRoute('/dashboard');

        if (!dashboardTab) {
            const result = openTab({
                title: 'Dashboard',
                route: '/dashboard',
                icon: 'LayoutDashboard',
                closeable: false,
            });

            // Marcar Dashboard como montado inmediatamente
            if (result.success) {
                const newTab = getTabByRoute('/dashboard');
                if (newTab) {
                    setMountedTabIds(prev => new Set(prev).add(newTab.id));
                }
            }
        } else {
            // Dashboard ya existe, asegurar que esté montado
            setMountedTabIds(prev => new Set(prev).add(dashboardTab.id));
        }
    }, []);

    /**
     * Cuando cambia el tab activo, marcarlo como montado
     */
    useEffect(() => {
        if (activeTabId && !mountedTabIds.has(activeTabId)) {
            setMountedTabIds(prev => new Set(prev).add(activeTabId));
        }
    }, [activeTabId, mountedTabIds]);

    /**
     * Auto-abrir tab cuando cambia la ruta
     */
    useEffect(() => {
        const currentRoute = location.pathname;
        const existingTab = getTabByRoute(currentRoute);

        if (existingTab) {
            if (activeTabId !== existingTab.id) {
                setActiveTab(existingTab.id);
            }
            return;
        }

        if (tabs.length > 0 || currentRoute === '/dashboard') {
            const title = getTabTitle?.(currentRoute) || 'Vista';
            const icon = getTabIcon?.(currentRoute);

            const result = openTab({
                title,
                route: currentRoute,
                icon,
                closeable: true,
            });

            if (!result.success && result.reason === 'limit') {
                showToast({
                    type: 'warning',
                    message: 'Has alcanzado el límite de pestañas. Cierra algunas para continuar.',
                });

                const activeTab = tabs.find((t) => t.id === activeTabId);
                if (activeTab) {
                    navigate(activeTab.route, { replace: true });
                }
            }
        }
    }, [location.pathname]);

    /**
     * Sincronizar URL cuando cambia tab activo
     */
    useEffect(() => {
        const activeTab = tabs.find((t) => t.id === activeTabId);
        if (activeTab && location.pathname !== activeTab.route) {
            navigate(activeTab.route, { replace: true });
        }
    }, [activeTabId]);

    /**
     * Handlers para TabBar
     */
    const handleTabClick = (tabId: string) => {
        setActiveTab(tabId);
    };

    const handleTabClose = (tabId: string) => {
        const shouldNavigateToDashboard = closeTab(tabId);
        if (shouldNavigateToDashboard) {
            navigate('/dashboard');
        }
    };

    const handleMenuAction = (action: any) => {
        const { openTab: openTabAction, closeAllTabs, closeOtherTabs } = useTabsStore.getState();

        switch (action.type) {
            case 'CLOSE_ALL':
                closeAllTabs();
                navigate('/dashboard');
                break;
            case 'CLOSE_OTHERS':
                closeOtherTabs(action.tabId);
                break;
            case 'CLOSE_RIGHT': {
                const activeIndex = tabs.findIndex(t => t.id === action.tabId);
                if (activeIndex !== -1) {
                    tabs.slice(activeIndex + 1).forEach(tab => {
                        if (tab.closeable) {
                            closeTab(tab.id);
                        }
                    });
                }
                break;
            }
            case 'RELOAD_ACTIVE': {
                const tab = tabs.find(t => t.id === action.tabId);
                if (tab && tab.closeable) {
                    closeTab(tab.id);
                    setTimeout(() => {
                        openTabAction({
                            title: tab.title,
                            route: tab.route,
                            icon: tab.icon,
                            closeable: tab.closeable,
                        });
                        navigate(tab.route);
                    }, 50);
                }
                break;
            }
        }
    };

    return (
        <div className={styles.tabContainer}>
            <TabBar
                tabs={tabs}
                activeTabId={activeTabId}
                isSidebarCollapsed={isSidebarCollapsed}
                onTabClick={handleTabClick}
                onTabClose={handleTabClose}
                onMenuAction={handleMenuAction}
            />

            <div className={styles.tabContent}>
                {tabs.map((tab) => {
                    const isActive = tab.id === activeTabId;
                    const isMounted = mountedTabIds.has(tab.id);

                    // CRÍTICO: Solo renderizar si el tab ha sido activado al menos una vez
                    // Esto evita montar todos los componentes simultáneamente
                    if (!isMounted) {
                        return null;
                    }

                    const Component = getComponentForRoute(tab.route);

                    if (!Component) {
                        console.warn(`No component mapped for route: ${tab.route}`);
                        return null;
                    }

                    return (
                        <TabPanel key={tab.id} tabId={tab.id} isActive={isActive}>
                            <Suspense fallback={<div className={styles.loading}>Cargando...</div>}>
                                {/* 
                  Componente montado solo cuando el tab se activa por primera vez
                  Una vez montado, permanece montado (preserva estado)
                  display:none lo oculta cuando no está activo
                */}
                                <Component />
                            </Suspense>
                        </TabPanel>
                    );
                })}
            </div>
        </div>
    );
};
